import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTelegram } from '../context/TelegramContext';
import { useCurrency } from '../context/CurrencyContext';
import Layout from '../components/layout/Layout';
import GoalsList from '../components/goals/GoalsList';
import GoalModal from '../components/goals/GoalModal';
import SharedDataToggle from '../components/statistics/SharedDataToggle';

const NoZoomContainer = styled.div`
  touch-action: manipulation;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
`;

const Container = styled.div`
  padding: 16px;
  max-width: 800px;
  margin: 0 auto;
  touch-action: manipulation;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #4a6fa1;
  margin: 0;
  
  @media (max-width: 576px) {
    font-size: 20px;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  background: linear-gradient(145deg, #f0f3f9, #e6ebf5);
  color: #4a6fa1;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.05),
              -2px -2px 5px rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: linear-gradient(145deg, #e6ebf5, #dde2ec);
    transform: translateY(-2px);
    box-shadow: 3px 3px 7px rgba(0, 0, 0, 0.07),
                -3px -3px 7px rgba(255, 255, 255, 0.9);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.1),
                inset -1px -1px 3px rgba(255, 255, 255, 0.9);
  }
`;

const AddButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px 20px;
  margin-top: 20px;
  background: linear-gradient(145deg, #4a6fa1, #5d82b3);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  text-align: center;
  cursor: pointer;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1),
              -2px -2px 5px rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  
  &:hover, &:focus {
    background: linear-gradient(145deg, #3d5e8a, #4a6fa1);
    transform: translateY(-2px);
    box-shadow: 3px 3px 7px rgba(0, 0, 0, 0.15),
                -3px -3px 7px rgba(255, 255, 255, 0.15);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: inset 1px 1px 3px rgba(0, 0, 0, 0.2),
                inset -1px -1px 3px rgba(255, 255, 255, 0.2);
  }
`;

const Goals = () => {
  const { t } = useTranslation();
  const { telegramUserId } = useTelegram();
  const { userCurrency } = useCurrency();
  
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [includeSharedGoals, setIncludeSharedGoals] = useState(true);
  const [hasLinkedAccounts, setHasLinkedAccounts] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null); // Добавляем состояние для редактирования
  const [currentCurrency, setCurrentCurrency] = useState(userCurrency || 'USD');
  
  // Блокируем приближение при загрузке компонента
  useEffect(() => {
    // Добавляем или обновляем мета-тег viewport
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.name = 'viewport';
      document.head.appendChild(viewport);
    }
    viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
    
    // Блокируем жесты масштабирования
    const preventZoom = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };
    
    const preventDoubleTapZoom = (e) => {
      const t2 = e.timeStamp;
      const t1 = e.currentTarget.dataset.lastTouch || t2;
      const dt = t2 - t1;
      const fingers = e.touches.length;
      
      e.currentTarget.dataset.lastTouch = t2;
      
      if (!dt || dt > 500 || fingers > 1) return;
      
      e.preventDefault();
      e.target.click();
    };
    
    document.addEventListener('touchstart', preventZoom, { passive: false });
    document.addEventListener('touchstart', preventDoubleTapZoom, { passive: false });
    
    return () => {
      document.removeEventListener('touchstart', preventZoom);
      document.removeEventListener('touchstart', preventDoubleTapZoom);
    };
  }, []);

  // Отслеживаем изменения валюты
  useEffect(() => {
    if (userCurrency && userCurrency !== currentCurrency) {
      setCurrentCurrency(userCurrency);
    }
  }, [userCurrency, currentCurrency]);
  
  // Проверяем наличие связанных аккаунтов
  useEffect(() => {
    const checkLinkedAccounts = async () => {
      if (!telegramUserId) return;

      try {
        const response = await fetch(
          `https://fftg.farodinprojects.org:3000/get_linked_accounts?user_id=${telegramUserId}`
        );
        const accounts = await response.json();
        setHasLinkedAccounts(Array.isArray(accounts) && accounts.length > 0);
      } catch (error) {
        console.error('[Goals] Error checking linked accounts:', error);
        setHasLinkedAccounts(false);
      }
    };

    checkLinkedAccounts();
  }, [telegramUserId]);
  
  // Загружаем цели
  const loadGoals = async () => {
    if (!telegramUserId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://fftg.farodinprojects.org:3000/goals?user_id=${telegramUserId}&shared=${includeSharedGoals ? 1 : 0}`
      );
      
      if (!response.ok) {
        throw new Error(`Error loading goals: ${response.statusText}`);
      }
      
      const data = await response.json();
      setGoals(data);
    } catch (error) {
      console.error('[Goals] Error loading goals:', error);
      setGoals([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Загружаем цели при изменении состояния включения общих целей
  useEffect(() => {
    loadGoals();
  }, [telegramUserId, includeSharedGoals]);
  
  // Обработчик добавления новой цели
  const handleAddGoal = async (goalData) => {
    try {
      const response = await fetch('https://fftg.farodinprojects.org:3000/add_goal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: telegramUserId,
          ...goalData,
          shared: includeSharedGoals
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        loadGoals();
        return true;
      } else {
        console.error('[Goals] Error adding goal:', data.error);
        return false;
      }
    } catch (error) {
      console.error('[Goals] Error adding goal:', error);
      return false;
    }
  };
  
  // Обработчик удаления цели
  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm(t('confirm_delete'))) return;
    
    try {
      const response = await fetch('https://fftg.farodinprojects.org:3000/delete_goal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          goal_id: goalId,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        loadGoals();
        return true;
      } else {
        console.error('[Goals] Error deleting goal:', data.error);
        return false;
      }
    } catch (error) {
      console.error('[Goals] Error deleting goal:', error);
      return false;
    }
  };
   // Обработчик редактирования цели
  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setIsModalOpen(true);
  };

  // Обновленный обработчик добавления/редактирования цели
  const handleSubmitGoal = async (goalData) => {
    try {
      const url = editingGoal 
        ? 'https://fftg.farodinprojects.org:3000/update_goal'
        : 'https://fftg.farodinprojects.org:3000/add_goal';
      
      const body = editingGoal 
        ? { goal_id: editingGoal.id, ...goalData }
        : { user_id: telegramUserId, ...goalData, shared: includeSharedGoals };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      
      const data = await response.json();
      
      if (data.success) {
        loadGoals();
        return true;
      } else {
        console.error('[Goals] Error submitting goal:', data.error);
        return false;
      }
    } catch (error) {
      console.error('[Goals] Error submitting goal:', error);
      return false;
    }
  };

  // Обработчик закрытия модального окна
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingGoal(null);
  };
  // Обработчик обновления прогресса цели
  const handleUpdateProgress = async (goalId, amount, currency) => {
    try {
      const response = await fetch('https://fftg.farodinprojects.org:3000/update_goal_progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          goal_id: goalId,
          amount,
          currency
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        loadGoals();
        return true;
      } else {
        console.error('[Goals] Error updating goal progress:', data.error);
        return false;
      }
    } catch (error) {
      console.error('[Goals] Error updating goal progress:', error);
      return false;
    }
  };
  
  const handleToggleSharedGoals = () => {
    setIncludeSharedGoals(!includeSharedGoals);
  };
  
  return (
    <NoZoomContainer>
      <Layout>
        <Container>
          <PageHeader>
            <PageTitle>{t('goals')}</PageTitle>
            <BackButton to="/">
              {t('back_button')}
            </BackButton>
          </PageHeader>
          
          {hasLinkedAccounts && (
            <SharedDataToggle 
              includeSharedData={includeSharedGoals} 
              onToggle={handleToggleSharedGoals} 
            />
          )}
          
          <GoalsList 
            goals={goals} 
            isLoading={isLoading} 
            onUpdateProgress={handleUpdateProgress}
            onDeleteGoal={handleDeleteGoal}
            onEditGoal={handleEditGoal} // Добавляем prop для редактирования
            defaultCurrency={currentCurrency}
          />
          
          <AddButton onClick={() => setIsModalOpen(true)}>
            {t('add_goal')}
          </AddButton>
          
          <GoalModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSubmit={handleSubmitGoal}
            editingGoal={editingGoal} // Передаем редактируемую цель
            defaultCurrency={currentCurrency}
          />
        </Container>
      </Layout>
    </NoZoomContainer>
  );
};

export default Goals;